import { z } from 'zod';
import {
  projectMediaSchema,
  projectSurveySchema,
  submitProjectMediaSchema,
} from './projects';

export const EternalChallengePhase = {
  INACTIVE: 1,
  OPEN: 2,
  CHALLENGE_ENDED: 3,
  WINNER_ANNOUNCED: 4,
} as const;

export type EternalChallengePhase =
  (typeof EternalChallengePhase)[keyof typeof EternalChallengePhase];

export const EternalChallengeParticipationStatus = {
  NOT_STARTED: 1,
  AWAITING_PROJECT_DETAILS: 2,
  WEEK_1_IN_PROGRESS: 3,
  WEEK_1_SUBMISSION_OPEN: 4,
  WEEK_1_SUBMITTED: 5,
  WEEK_2_IN_PROGRESS: 6,
  WEEK_2_SUBMISSION_OPEN: 7,
  WEEK_2_SUBMITTED: 8,
  WEEK_3_IN_PROGRESS: 9,
  WEEK_3_SUBMISSION_OPEN: 10,
  WEEK_3_SUBMITTED: 11,
  WEEK_4_IN_PROGRESS: 12,
  WEEK_4_SUBMISSION_OPEN: 13,
  PROJECT_SUBMITTED: 14,
  TIMER_RAN_OUT: 15,
} as const;

export type EternalChallengeParticipationStatus =
  (typeof EternalChallengeParticipationStatus)[keyof typeof EternalChallengeParticipationStatus];

export function getEternalChallengePhaseLabel(
  phase: EternalChallengePhase,
): string {
  switch (phase) {
    case EternalChallengePhase.INACTIVE:
      return 'Inactive';
    case EternalChallengePhase.OPEN:
      return 'Open';
    case EternalChallengePhase.CHALLENGE_ENDED:
      return 'Challenge Ended';
    case EternalChallengePhase.WINNER_ANNOUNCED:
      return 'Winner Announced';
  }
}

export interface EternalChallenge {
  id: number;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  winnerAnnouncedDate: Date | null;
  largeBannerImageUrl: string;
  smallBannerImageUrl: string;
  landingPageUrl: string;
}

export interface EternalChallengeWithPhase extends EternalChallenge {
  phase: EternalChallengePhase;
}

export function isEternalChallengeActive(challenge: EternalChallengeWithPhase) {
  return challenge.phase === EternalChallengePhase.OPEN;
}

export type EternalChallengeCompletionChecklist = {
  week1UpdateSubmittedAt: Date | null;
  week2UpdateSubmittedAt: Date | null;
  week3UpdateSubmittedAt: Date | null;
  week4UpdateSubmittedAt: Date | null;
};

export type EternalWeekNumber = 1 | 2 | 3 | 4;

export type EternalWeeklyUpdateWeekNumber = 1 | 2 | 3;

export type UserEternalChallenge = {
  participationStatus: EternalChallengeParticipationStatus;
  details: UserEternalChallengeDetails | null;
};

type EternalWeeklyUpdate = {
  updateSubmittedAt: Date | null;
  opensAt: Date;
  dueAt: Date;
};

export type EternalWeeklyUpdates = Record<
  EternalWeeklyUpdateWeekNumber,
  EternalWeeklyUpdate
>;

export interface UserEternalChallengeDetails {
  id: number;
  userId: number;
  eternalChallengeId: number;
  startTime: Date;
  submissionDueDate: Date;
  projectId: number | null;
  submittedAt: Date | null;
  weeklyUpdates: EternalWeeklyUpdates;
}

export type EternalChallengeWithUserState = {
  eternalChallenge: EternalChallengeWithPhase;
  userChallenge: UserEternalChallenge;
};

export const eternalTeamMemberSchema = z.object({
  id: z.number().min(1, { message: 'Team member ID is required.' }),
});

export const eternalProjectAndTeamSchema = z.object({
  name: z.string().min(1, { message: 'Name is required.' }),
  description: z.string().min(1, { message: 'Description is required.' }),
  productCategories: z
    .array(z.string())
    .min(1, { message: 'At least one product category is required.' })
    .max(3, { message: 'You may only select up to 3 product categories.' }),
  otherProductCategory: z.string().nullable(),
  country: z.string().min(1, { message: 'Country is required.' }),
  teamMembers: z
    .array(eternalTeamMemberSchema)
    .min(1, { message: 'At least one team member is required.' }),
  repoLink: z.string().nullable(),
});

// Extend the base project media schema for eternal challenges
export const eternalProjectMediaSchema = projectMediaSchema.extend({
  isPublic: z.boolean().default(false),
});

export const submitEternalProjectAndTeamSchema =
  eternalProjectAndTeamSchema.extend({
    repoLink: z
      .string()
      .url()
      .min(1, { message: 'Repository link is required.' }),
  });

export const submitEternalProjectMediaSchema = submitProjectMediaSchema.extend({
  isPublic: z.boolean().default(false),
});

export const eternalProjectSurveySchema = projectSurveySchema;

export const createEternalProjectSchema = eternalProjectAndTeamSchema.merge(
  eternalProjectMediaSchema,
);

export const submitEternalProjectSchema =
  submitEternalProjectAndTeamSchema.merge(submitEternalProjectMediaSchema);

export const ETERNAL_PRODUCT_CATEGORIES = [
  'DePIN',
  'DeFi',
  'DAOs & Network States',
  'Payments',
  'Consumer',
  'Gaming',
  'Infrastructure',
] as const;

// Time constants for eternal challenge calculations
export const WEEK_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
export const WEEK_UPDATE_OPEN_TIME_OFFSET = 48 * 60 * 60 * 1000; // 48 hours in milliseconds

/**
 * Gets the phase of an eternal challenge based on its dates
 * @param challenge - The eternal challenge to get the phase for
 * @param currentDate - Optional current date for testing (defaults to now)
 * @returns The phase of the eternal challenge
 */
export function getEternalChallengePhase(
  challenge: EternalChallenge,
  currentDate: Date = new Date(),
): EternalChallengePhase {
  let phase: EternalChallengePhase;

  if (currentDate < challenge.startDate) {
    phase = EternalChallengePhase.INACTIVE;
  } else if (currentDate < challenge.endDate) {
    phase = EternalChallengePhase.OPEN;
  } else if (
    !challenge.winnerAnnouncedDate ||
    currentDate < challenge.winnerAnnouncedDate
  ) {
    phase = EternalChallengePhase.CHALLENGE_ENDED;
  } else {
    phase = EternalChallengePhase.WINNER_ANNOUNCED;
  }

  return phase;
}

/**
 * Calculate the current week number for an eternal challenge based on start time
 * @param startTime - The start time of the challenge
 * @param currentDate - The current date (defaults to now)
 * @returns The current week number (1-4)
 */
export function calculateEternalChallengeWeekNumber(
  startTime: Date,
  currentDate: Date = new Date(),
): EternalWeekNumber {
  const daysSinceStart = Math.floor(
    (currentDate.getTime() - startTime.getTime()) / (1000 * 60 * 60 * 24),
  );
  return Math.min(Math.floor(daysSinceStart / 7) + 1, 4) as EternalWeekNumber;
}

/**
 * Calculate opens at date for a specific week based on start time (48 hours before the due date)
 * @param startTime The start time of the challenge
 * @param weekNumber The week number
 * @returns Date object representing the opens at date
 */
export function calculateWeeklyUpdateOpensAt(
  startTime: Date,
  weekNumber: number,
): Date {
  return new Date(
    startTime.getTime() +
      (weekNumber * WEEK_DURATION - WEEK_UPDATE_OPEN_TIME_OFFSET),
  );
}

/**
 * Calculate due date for a specific week based on start time
 * @param startTime The start time of the challenge
 * @param weekNumber The week number
 * @returns Date object representing the due date
 */
export function calculateWeeklyUpdateDueDate(
  startTime: Date,
  weekNumber: number,
): Date {
  return new Date(startTime.getTime() + weekNumber * WEEK_DURATION);
}

/**
 * Helper to determine weekly status based on time and submissions
 * @param weekNum - Week number (1-4)
 * @param startTime - Challenge start time
 * @param isSubmitted - Whether the submission was completed
 * @param submittedStatus - Status to use if submitted
 * @param openStatus - Status to use if open for submission
 * @param inProgressStatus - Status to use if in progress
 * @param currentTime - Current time (defaults to now)
 * @returns The appropriate participation status
 */
export function getWeeklyParticipationStatus(
  weekNum: number,
  startTime: Date,
  isSubmitted: boolean,
  submittedStatus: EternalChallengeParticipationStatus,
  openStatus: EternalChallengeParticipationStatus,
  inProgressStatus: EternalChallengeParticipationStatus,
  currentTime: Date = new Date(),
): EternalChallengeParticipationStatus {
  const weekEndTime = new Date(startTime.getTime() + weekNum * WEEK_DURATION);

  const submissionOpenTime = new Date(
    weekEndTime.getTime() - WEEK_UPDATE_OPEN_TIME_OFFSET,
  );

  return isSubmitted
    ? submittedStatus
    : currentTime >= submissionOpenTime
      ? openStatus
      : inProgressStatus;
}
